<template>
    <vx-modal
        v-model:visible="visible"
        padding="standard"
        size="xl"
        :preventClosing="preventClosing"
        :stacked="preventClosing"
    >
        <template v-slot:header> Scheduled Messages </template>
        <template v-slot:subheader>
            These messages are scheduled in the future for <strong>{{ contactName(contact) }}</strong>
        </template>

        <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <vx-alert
                v-if="deletedScheduledMessage && !scheduledMessagesError"
                color="success"
                shadow
                :closeable="false"
                class="col-span-full"
            >
                <strong>Message</strong> deleted successfully
                <template v-slot:right>
                    <vx-button
                        @click="undoDeletedMessage"
                        data-test="undo-deleted-message"
                        :loading="undoingDeletedMessage"
                        size="sm"
                        color="success"
                    >
                        Undo
                        <font-awesome-icon widthAuto :icon="faUndo"></font-awesome-icon>
                    </vx-button>
                </template>
            </vx-alert>

            <vx-alert
                v-if="scheduledMessagesError && !loadingScheduledMessages"
                class="col-span-full"
                color="danger"
                shadow
                :closeable="false"
            >
                <div>{{ scheduledMessagesError }}</div>
                <template v-slot:right>
                    <vx-button @click="retry()" :loading="undoingDeletedMessage" color="danger" size="sm">
                        Retry <font-awesome-icon widthAuto :icon="faRefresh"></font-awesome-icon>
                    </vx-button>
                </template>
            </vx-alert>

            <vx-alert
                v-if="!scheduledMessages.length && !loadingScheduledMessages"
                class="col-span-full"
                :closeable="false"
                >There are no scheduled messages for this contact.</vx-alert
            >

            <div v-if="loadingScheduledMessages && !scheduledMessages.length" class="col-span-full">
                <page-loading spin></page-loading>
            </div>

            <scheduled-message-card
                v-for="(scheduledMessage, index) in scheduledMessages"
                ref="cardsRef"
                v-model="scheduledMessages[index]"
                :key="scheduledMessage.id"
                data-test="scheduled-message-card"
                :contact="contact"
                @deleted="removeFromList"
            >
            </scheduled-message-card>

            <div v-if="scheduledMessagesParams.cursor" class="col-span-full">
                <vx-button
                    @click="getScheduledMessages()"
                    color="muted-dark"
                    size="xl"
                    class="grow-0"
                    data-test="load-more-btn"
                    :disabled="loadingScheduledMessages"
                    :loading="loadingScheduledMessages"
                >
                    Load More
                    <font-awesome-icon widthAuto :icon="faSpinner"></font-awesome-icon>
                </vx-button>
            </div>
        </div>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 lg:flex-row">
                <vx-button
                    @click="visible = false"
                    type="button"
                    class="grow lg:w-20 lg:grow-0"
                    size="lg"
                    color="muted"
                >
                    Cancel
                </vx-button>
                <vx-button @click="visible = false" type="button" class="grow" size="lg" color="primary">
                    Ok
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>
<script setup>
import { faRefresh, faSpinner, faUndo } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxAlert, VxButton, VxModal } from '@voxie/frontend-components';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useContactDetails } from '~/components/contacts/contact/useContactDetails';
import { contactName } from '~/components/filters';
import PageLoading from '~/components/general/PageLoading.vue';
import ScheduledMessageCard from '~/components/scheduled-messages/ScheduledMessageCard.vue';
import { scheduledMessagesClient } from '~/services';
import { isAuthErrorEcho } from '~/utils/helpers';
import { catchRequestError } from '~/utils/request';

const {
    contact,
    getScheduledMessages,
    loadingScheduledMessages,
    scheduledMessages,
    scheduledMessagesError,
    scheduledMessagesParams,
} = useContactDetails();

const visible = defineModel('visible', {
    type: Boolean,
    default: false,
});

const cardsRef = ref([]);

const preventClosing = computed(() => cardsRef.value.some((cardRef) => cardRef.editModal || cardRef.deleting));
const channelName = computed(() => {
    if (!contact.value.team_id) {
        return null;
    }

    let name = `scheduled_messages.${contact.value.team_id}`;

    if (contact.value.group_id) {
        name += `.${contact.value.group_id}`;
    }

    return name;
});

const deletedScheduledMessage = ref();
const removeFromList = (deletedItem) => {
    scheduledMessages.value = scheduledMessages.value.filter(
        (scheduledMessage) => scheduledMessage.id !== deletedItem.id
    );
    deletedScheduledMessage.value = deletedItem;
    if (!scheduledMessages.value.length) {
        getScheduledMessages();
    }
};

const undoingDeletedMessage = ref(false);
const undoDeletedMessage = async () => {
    if (!deletedScheduledMessage.value || undoingDeletedMessage.value) {
        return;
    }

    undoingDeletedMessage.value = true;
    try {
        const response = await scheduledMessagesClient.createScheduledMessage(
            { teamId: contact.value.team_id, groupId: contact.value.group_id },
            contact.value.id,
            {
                ...deletedScheduledMessage.value,
            }
        );

        scheduledMessages.value.unshift(response.data);
        scheduledMessagesError.value = '';
        deletedScheduledMessage.value = null;
    } catch (error) {
        scheduledMessagesError.value = catchRequestError(error);
    } finally {
        undoingDeletedMessage.value = false;
    }
};

const retry = () => {
    if (deletedScheduledMessage.value) {
        undoDeletedMessage();
    } else {
        getScheduledMessages();
    }
};

onMounted(() => {
    deletedScheduledMessage.value = null;
    getScheduledMessages();

    if (!channelName.value) {
        return;
    }

    const echoErrors = [];

    Echo.private(channelName.value)
        .listen('ScheduledMessageCreated', ({ contact: payloadContact, scheduled_message, user }) => {
            // Delay to ensure server state is updated before adding the new scheduled message.
            // This avoids race conditions where the client receives the event before the server processes it.
            // TODO: Replace this workaround when an update endpoint for scheduled messages is available.
            setTimeout(() => {
                if (
                    payloadContact.id === contact.value.id &&
                    !scheduledMessages.value.some((m) => m.id === scheduled_message.id)
                ) {
                    scheduledMessages.value.push({
                        ...scheduled_message,
                        contact: payloadContact,
                        user: user,
                    });
                }
            }, 1000);
        })
        .listen('ScheduledMessageEnqueued', ({ scheduled_message }) => {
            if (scheduled_message.contact_id === contact.value.id) {
                scheduledMessages.value = scheduledMessages.value.filter((m) => m.id !== scheduled_message.id);
            }
        })
        .listen('ScheduledMessageCancelled', ({ data: { scheduled_message } }) => {
            if (scheduled_message.contact_id === contact.value.id) {
                scheduledMessages.value = scheduledMessages.value.filter((m) => m.id !== scheduled_message.id);
            }
        })
        .error?.((e) => {
            if (echoErrors.length <= 3 && !isAuthErrorEcho(e)) {
                window?.Bugsnag?.notify(e instanceof Error ? e : new Error(JSON.stringify(e)));
                echoErrors.push(e);
            }
        });
});

onUnmounted(() => {
    if (channelName.value) {
        Echo.leave(channelName.value);
    }
});
</script>
